import { cn } from '@/lib/utils';
import { useElementWidth } from '@/hooks/useElementWidth';
import {
  allWhole,
  ChartDataTable,
  ChartLegend,
  ChartTooltip,
  labelEvery,
  plotLeftFor,
  showsAxisLabel,
  niceScale,
  PLOT,
  SERIES_TONES,
  useChartCursor,
  type ChartPoint,
  type ChartSeries,
} from './chartParts';

export interface BarChartProps<K extends string> {
  /** What the chart shows — announced, and the caption of its data table. */
  title: string;
  series: ChartSeries<K>[];
  points: ChartPoint<K>[];
  /** Plot height in px. */
  height?: number;
  formatValue?: (value: number) => string;
  /**
   * Thin the x labels from the last point back, so the latest is always named.
   * For timelines that end today; categorical charts leave it off.
   */
  labelFromEnd?: boolean;
  className?: string;
}

/** Bars are capped at this thickness; the rest of each slot is air. */
const MAX_BAR = 24;
/** The surface-coloured gap between stacked segments. */
const SEGMENT_GAP = 2;
/** Rounding on a bar's data end; the baseline stays square. */
const END_RADIUS = 4;

/**
 * Stacked columns over time — one column per day, one segment per series.
 *
 * Hand-drawn SVG like the rest of the product's charts (`DonutChart`,
 * `Sparkline`): no charting dependency, and the series colours are theme
 * tokens, so dark mode is its own validated palette rather than a flip.
 *
 * Laid out in real pixels from the measured width, so bar ends stay round and
 * lines stay hairlines at every screen size. Hover or arrow keys move a
 * cursor across the days; the tooltip lists every series and the total. The
 * drawing is hidden from screen readers, which get the same numbers as a table.
 */
export function BarChart<K extends string>({
  title,
  series,
  points,
  height = 200,
  formatValue = (value) => value.toLocaleString('en-IN'),
  labelFromEnd = false,
  className,
}: BarChartProps<K>) {
  const { ref, width } = useElementWidth<HTMLDivElement>();

  const totals = points.map((point) =>
    series.reduce((sum, item) => sum + (point.values[item.key] ?? 0), 0),
  );
  const { top, ticks } = niceScale(Math.max(0, ...totals), 4, allWhole(totals));

  const plotLeft = plotLeftFor(ticks.map(formatValue));
  const plotWidth = Math.max(0, width - plotLeft - PLOT.right);
  const plotHeight = height - PLOT.top - PLOT.bottom;
  const step = points.length > 0 ? plotWidth / points.length : 0;
  const barWidth = Math.max(2, Math.min(MAX_BAR, step * 0.62));
  const y = (value: number) => PLOT.top + plotHeight - (value / top) * plotHeight;
  const every = labelEvery(points.length, plotWidth);

  const { active, handlers } = useChartCursor(points.length, plotLeft, step);

  return (
    <figure className={cn('flex flex-col gap-3', className)}>
      <ChartLegend series={series} />

      <div
        ref={ref}
        className="relative rounded-field focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
        tabIndex={0}
        aria-label={`${title}. Use the arrow keys to read each day.`}
        {...handlers}
      >
        {width > 0 && (
          <svg width={width} height={height} aria-hidden className="block overflow-visible">
            {/* Recessive hairline grid with its labels. */}
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={plotLeft}
                  x2={width - PLOT.right}
                  y1={y(tick)}
                  y2={y(tick)}
                  className="stroke-line"
                  strokeWidth={1}
                />
                <text
                  x={plotLeft - 6}
                  y={y(tick)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  className="fill-ink-3 text-2xs tabular-nums"
                >
                  {formatValue(tick)}
                </text>
              </g>
            ))}

            {active !== null && (
              <rect
                x={plotLeft + active * step}
                y={PLOT.top}
                width={step}
                height={plotHeight}
                className="fill-surface-3"
              />
            )}

            {points.map((point, index) => {
              const x = plotLeft + index * step + (step - barWidth) / 2;
              const visible = series.filter((item) => (point.values[item.key] ?? 0) > 0);
              let base = 0;

              return (
                <g key={point.label}>
                  {visible.map((item, position) => {
                    const value = point.values[item.key] ?? 0;
                    const isTop = position === visible.length - 1;
                    const y0 = y(base);
                    const y1 = y(base + value);
                    base += value;
                    // A surface gap above every segment that has another on it.
                    const segmentTop = isTop ? y1 : y1 + SEGMENT_GAP;
                    const h = Math.max(0, y0 - segmentTop);
                    const r = isTop ? Math.min(END_RADIUS, h, barWidth / 2) : 0;

                    return (
                      <path
                        key={item.key}
                        className={SERIES_TONES[item.tone].fill}
                        d={
                          // Rounded at the data end only; square on the baseline.
                          `M${x},${y0} V${segmentTop + r} Q${x},${segmentTop} ${x + r},${segmentTop} ` +
                          `H${x + barWidth - r} Q${x + barWidth},${segmentTop} ${x + barWidth},${segmentTop + r} ` +
                          `V${y0} Z`
                        }
                      />
                    );
                  })}

                  {showsAxisLabel(index, points.length, every, labelFromEnd) && (
                    <text
                      x={plotLeft + index * step + step / 2}
                      y={height - 6}
                      textAnchor="middle"
                      className="fill-ink-3 text-xs"
                    >
                      {point.axisLabel ?? point.label}
                    </text>
                  )}
                </g>
              );
            })}

            <line
              x1={plotLeft}
              x2={width - PLOT.right}
              y1={y(0)}
              y2={y(0)}
              className="stroke-line-strong"
              strokeWidth={1}
            />
          </svg>
        )}

        {active !== null && points[active] && (
          <ChartTooltip
            point={points[active]}
            series={series}
            formatValue={formatValue}
            x={plotLeft + active * step + step / 2}
            width={width}
            total={series.length > 1 ? totals[active] : undefined}
          />
        )}
      </div>

      <ChartDataTable caption={title} points={points} series={series} formatValue={formatValue} />
    </figure>
  );
}
